// Markdown → HTML parser for the enriched editor's wire format.
//
// Dialect: the same mrkdwn-ish format the existing toolbar emits.
//   - `*bold*` `_italic_` `~strike~` `` `code` `` inline marks
//   - `> ` blockquote line
//   - `- ` (or `* `) bullet line
//   - `1. ` numbered line
//   - 2-space indentation per nesting level for lists
//   - ` ``` ` fenced code block
//   - `[label](url)` link
//
// Block grouping: adjacent same-kind lines collapse into a single
// visual block. A line like `> foo` followed by `> bar` produces one
// `<blockquote>foo<br>bar</blockquote>`; the corresponding emit path
// in `serializer.ts` splits it back. Empty lines force a block break.

const INLINE_MARK_CHARS: Readonly<Record<string, string>> = {
  '*': 'strong',
  _: 'em',
  '~': 's',
  '`': 'code',
};

type ListKind = 'bullet' | 'numbered';

interface LineToken {
  readonly kind: 'paragraph' | 'quote' | 'bullet' | 'numbered' | 'code';
  readonly content: string;
  /** Leading-space count, in spaces. 2-space indent per nesting level. */
  readonly indent: number;
}

/**
 * Convert a markdown source string into an HTML string suitable for
 * dropping into a `contenteditable` div's innerHTML. The output is
 * intentionally minimal — semantic tags only, no inline styles, no
 * attributes other than `<a href>`.
 */
export function markdownToHtml(markdown: string): string {
  const lines = markdown.split('\n');
  const blocks: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';

    const fenceOpen = /^(`{3,})(.*)$/.exec(line.trimStart());
    if (fenceOpen !== null) {
      // CommonMark variable-length fence: remember the opening fence length
      // and only close on a bare fence of AT LEAST that length. This lets a
      // NESTED code block (an inner ``` inside the body) survive a
      // round-trip instead of closing the outer block early — matching the
      // serializer (`markdown/serializer.ts`) and the tile parser
      // (`packages/ui/src/chat/mrkdwn/parseMrkdwn.ts`).
      const fenceLen = (fenceOpen[1] ?? '```').length;
      // Optional language label after the opening fence (``` ```ts ```).
      const lang = (fenceOpen[2] ?? '').trim().toLowerCase();
      const start = i + 1;
      let end = start;
      const closeRe = /^(`{3,})\s*$/;
      while (end < lines.length) {
        const cm = closeRe.exec((lines[end] ?? '').trimStart());
        if (cm !== null && (cm[1] ?? '').length >= fenceLen) break;
        end++;
      }
      const inner = lines.slice(start, end).join('\n');
      // Only allow a safe, simple language token onto the attribute.
      const safeLang = /^[a-z0-9+#.-]{1,24}$/.test(lang) ? lang : '';
      const langAttr = safeLang.length > 0 ? ` data-lang="${safeLang}"` : '';
      // Preserve the opening fence length so a nested (4+ backtick) block
      // stays nestable after a reload / edit — the editor's exit logic reads
      // `data-fence` to decide what closes it.
      const fenceAttr = fenceLen > 3 ? ` data-fence="${fenceLen}"` : '';
      blocks.push(
        `<pre${langAttr}${fenceAttr}><code>${escapeText(inner)}</code></pre>`,
      );
      i = end + 1;
      continue;
    }

    const tok = classifyLine(line);
    if (tok.kind === 'quote') {
      const group: string[] = [];
      while (i < lines.length) {
        const t = classifyLine(lines[i] ?? '');
        if (t.kind !== 'quote') break;
        group.push(inlineToHtml(t.content));
        i++;
      }
      blocks.push(`<blockquote>${group.join('<br>')}</blockquote>`);
      continue;
    }

    if (tok.kind === 'bullet' || tok.kind === 'numbered') {
      // Greedy: consume every consecutive line that's a list line of
      // either kind; build a single tree honoring 2-space indentation.
      const listLines: LineToken[] = [];
      while (i < lines.length) {
        const t = classifyLine(lines[i] ?? '');
        if (t.kind !== 'bullet' && t.kind !== 'numbered') break;
        listLines.push(t);
        i++;
      }
      blocks.push(buildListHtml(listLines));
      continue;
    }

    // Paragraph — accumulate adjacent non-empty plain lines into one
    // `<p>` separated by `<br>`. An empty line ends the paragraph.
    if (line === '') {
      // A blank line is a REAL empty line (the user pressed Enter on an empty
      // line) — emit an empty paragraph so it renders and round-trips. The old
      // "skip it" behavior silently dropped blank lines: it collapsed
      // "a\n\nb" → "a\nb" on a draft/edit reload, and made a seeded blank body
      // (e.g. the saved-message modal's `"\n\n\n"` default) disappear. The
      // serializer already emits an empty line per `<p><br></p>`, so this makes
      // the markdown ↔ HTML round-trip symmetric.
      blocks.push('<p><br></p>');
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const cur = lines[i] ?? '';
      // A fenced code block starts a new block even with no blank line
      // before it — don't swallow the opening ``` into this paragraph.
      if (/^```/.test(cur.trimStart())) break;
      const c = classifyLine(cur);
      if (c.kind !== 'paragraph' || cur === '') break;
      para.push(inlineToHtml(c.content));
      i++;
    }
    blocks.push(`<p>${para.join('<br>')}</p>`);
  }

  return blocks.join('');
}

function classifyLine(line: string): LineToken {
  // Count leading spaces (tabs treated as 2 spaces — same as Slack).
  let indent = 0;
  for (const ch of line) {
    if (ch === ' ') indent++;
    else if (ch === '\t') indent += 2;
    else break;
  }
  const stripped = line.slice(indent);
  if (stripped.startsWith('> ')) return { kind: 'quote', content: stripped.slice(2), indent };
  if (stripped === '>') return { kind: 'quote', content: '', indent };
  if (stripped.startsWith('- ')) return { kind: 'bullet', content: stripped.slice(2), indent };
  if (stripped.startsWith('* ')) return { kind: 'bullet', content: stripped.slice(2), indent };
  const num = stripped.match(/^(\d+)\.\s(.*)$/);
  if (num !== null) return { kind: 'numbered', content: num[2] ?? '', indent };
  return { kind: 'paragraph', content: line, indent };
}

/**
 * Build the HTML for a sequence of list-line tokens, honoring
 * 2-space indentation per nesting level. Sibling items at the same
 * depth share the same `<ul>`/`<ol>`; a deeper indent opens a nested
 * list inside the previous sibling.
 */
function buildListHtml(lines: ReadonlyArray<LineToken>): string {
  if (lines.length === 0) return '';
  // Tree node: a list at one indent level.
  interface ListNode {
    readonly kind: ListKind;
    readonly items: ItemNode[];
  }
  interface ItemNode {
    content: string;
    children: ListNode[];
  }
  const root: ListNode = {
    kind: lines[0]?.kind === 'numbered' ? 'numbered' : 'bullet',
    items: [],
  };
  // Stack of (depth, list) so we can find the right list to push
  // each new item into.
  const stack: Array<{ depth: number; list: ListNode }> = [
    { depth: lines[0]?.indent ?? 0, list: root },
  ];

  for (const line of lines) {
    if (line.kind !== 'bullet' && line.kind !== 'numbered') continue;
    const kind: ListKind = line.kind;
    // Pop deeper levels off the stack.
    while (stack.length > 1 && (stack[stack.length - 1]?.depth ?? 0) > line.indent) {
      stack.pop();
    }
    const top = stack[stack.length - 1];
    if (top === undefined) break;
    if (line.indent > top.depth) {
      // New deeper level. Attach a fresh list to the previous item's
      // children; if there's no previous item at the current level
      // (shouldn't happen with well-formed input), open a synthetic
      // list at the current level instead.
      const parentItem = top.list.items[top.list.items.length - 1];
      if (parentItem !== undefined) {
        const nested: ListNode = { kind, items: [{ content: line.content, children: [] }] };
        parentItem.children.push(nested);
        stack.push({ depth: line.indent, list: nested });
        continue;
      }
    }
    // Same depth. If the list kind switched, open a new sibling list
    // at the same level — the caller will render them as two separate
    // <ul>/<ol> elements.
    if (top.list.kind !== kind) {
      // Need a sibling list. Walk up to whoever owns `top.list` to
      // attach a new sibling — for the root level that's the caller.
      // We model it by replacing the top of the stack with a fresh
      // list and rendering both at flush time.
      const sibling: ListNode = { kind, items: [{ content: line.content, children: [] }] };
      // Trick: stash sibling lists by chaining onto the prior list
      // through a synthetic "next" reference rendered later.
      (top.list as ListNode & { next?: ListNode }).next = sibling;
      stack[stack.length - 1] = { depth: top.depth, list: sibling };
      continue;
    }
    top.list.items.push({ content: line.content, children: [] });
  }

  // Render the chain of root-level lists.
  let html = '';
  let cur: ListNode | undefined = root;
  while (cur !== undefined) {
    html += renderList(cur);
    cur = (cur as ListNode & { next?: ListNode }).next;
  }
  return html;

  function renderList(list: ListNode): string {
    const tag = list.kind === 'bullet' ? 'ul' : 'ol';
    const inner = list.items
      .map((item) => {
        let nested = '';
        let c: ListNode | undefined = item.children[0];
        // Render any sibling-chained lists too.
        while (c !== undefined) {
          nested += renderList(c);
          c = (c as ListNode & { next?: ListNode }).next;
        }
        for (let k = 1; k < item.children.length; k++) {
          const child = item.children[k];
          if (child !== undefined) nested += renderList(child);
        }
        return `<li>${inlineToHtml(item.content)}${nested}</li>`;
      })
      .join('');
    return `<${tag}>${inner}</${tag}>`;
  }
}

/**
 * Convert one line's inline marks into HTML. Walks character-by-
 * character so the implementation tolerates unbalanced markers
 * (an unmatched `*` at end of line is treated as literal text).
 */
export function inlineToHtml(line: string): string {
  // First pass: extract links so the inline-mark walker doesn't
  // interpret `*`/`_` inside link labels or URLs.
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  const segments: Array<{ kind: 'text' | 'link'; value: string; href?: string }> = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = linkRe.exec(line)) !== null) {
    if (m.index > cursor) {
      segments.push({ kind: 'text', value: line.slice(cursor, m.index) });
    }
    segments.push({ kind: 'link', value: m[1] ?? '', href: m[2] ?? '' });
    cursor = m.index + m[0].length;
  }
  if (cursor < line.length) {
    segments.push({ kind: 'text', value: line.slice(cursor) });
  }

  return segments
    .map((seg) => {
      if (seg.kind === 'link') {
        return `<a href="${escapeAttr(seg.href ?? '')}">${escapeText(seg.value)}</a>`;
      }
      return inlineMarksToHtml(seg.value);
    })
    .join('');
}

function inlineMarksToHtml(text: string): string {
  let out = '';
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch !== undefined && ch in INLINE_MARK_CHARS) {
      // Look ahead for the matching closer on the same line.
      const close = text.indexOf(ch, i + 1);
      if (close > i + 1) {
        const tag = INLINE_MARK_CHARS[ch] ?? '';
        const inner = text.slice(i + 1, close);
        // For inline code we don't recurse — backticks suppress
        // other mark interpretation between them.
        const innerHtml =
          tag === 'code' ? escapeText(inner) : inlineMarksToHtml(inner);
        out += `<${tag}>${innerHtml}</${tag}>`;
        i = close + 1;
        continue;
      }
    }
    out += escapeText(ch ?? '');
    i++;
  }
  return out;
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return escapeText(s).replace(/"/g, '&quot;');
}
